import * as React from 'react'

import type {
  ToastActionElement,
  ToastProps,
  ToastVariant,
} from '@/components/ui/toast'

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 1000

type ExtendedToastVariant = ToastVariant | 'success' | 'warning'

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  variant?: ExtendedToastVariant
}

type ToastInput = Omit<ToasterToast, 'id'>

type State = {
  toasts: ToasterToast[]
}

type Action =
  | { type: 'ADD_TOAST'; toast: ToasterToast }
  | { type: 'UPDATE_TOAST'; toast: Partial<ToasterToast> & { id: string } }
  | { type: 'DISMISS_TOAST'; toastId?: string }
  | { type: 'REMOVE_TOAST'; toastId?: string }

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({ type: 'REMOVE_TOAST', toastId })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

const toastReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map(toast =>
          toast.id === action.toast.id ? { ...toast, ...action.toast } : toast
        ),
      }

    case 'DISMISS_TOAST': {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach(toast => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map(toast =>
          toast.id === toastId || toastId === undefined
            ? { ...toast, open: false }
            : toast
        ),
      }
    }

    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }

      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.toastId),
      }
  }
}

const listeners = new Set<(state: State) => void>()

let memoryState: State = { toasts: [] }

const dispatch = (action: Action) => {
  memoryState = toastReducer(memoryState, action)
  listeners.forEach(listener => {
    listener(memoryState)
  })
}

let count = 0
const genId = () => {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

const normalizeVariant = (
  variant: ExtendedToastVariant | undefined
): ToastVariant | undefined => {
  switch (variant) {
    case 'success':
      return 'success'
    case 'warning':
      return 'warning'
    case 'destructive':
      return 'destructive'
    default:
      return variant ?? 'default'
  }
}

export function toast(props: ToastInput) {
  const id = genId()
  const normalizedVariant = normalizeVariant(props.variant)

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      variant: normalizedVariant,
    },
  })

  return {
    id,
    dismiss: () => dispatch({ type: 'DISMISS_TOAST', toastId: id }),
    update: (updatedProps: Partial<ToastInput>) =>
      dispatch({
        type: 'UPDATE_TOAST',
        toast: { id, ...updatedProps },
      }),
  }
}

// Helper functions for common toast patterns
toast.success = (message: string, title?: string, duration?: number) => {
  return toast({
    title: title || 'Success',
    description: message,
    variant: 'success',
    duration: duration || 3000, // Auto-dismiss after 3 seconds
  })
}

toast.error = (message: string, title?: string, duration?: number) => {
  return toast({
    title: title || 'Error',
    description: message,
    variant: 'destructive',
    duration: duration || 5000, // Auto-dismiss after 5 seconds (errors stay longer)
  })
}

toast.warning = (message: string, title?: string, duration?: number) => {
  return toast({
    title: title || 'Warning',
    description: message,
    variant: 'warning',
    duration: duration || 4000, // Auto-dismiss after 4 seconds
  })
}

toast.info = (message: string, title?: string, duration?: number) => {
  return toast({
    title: title || 'Info',
    description: message,
    variant: 'default',
    duration: duration || 3000, // Auto-dismiss after 3 seconds
  })
}

export function dismiss(toastId?: string) {
  dispatch({ type: 'DISMISS_TOAST', toastId })
}

export function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.add(setState)
    return () => {
      listeners.delete(setState)
    }
  }, [])

  return {
    ...state,
    toast,
    dismiss,
  }
}
